import * as Crypto from 'expo-crypto';
import { api } from './api';
import type {
    ContourErrorCode,
    PayoutDetailsStatus,
    PayoutDetailsSubmission,
    PayoutDetailsTicket,
} from '../types/taxContour';

/**
 * CORE-1053 — разговор с российским контуром ПДн.
 *
 * <p>Отдельный модуль, а не метод в `api.ts`, потому что это другой хост и
 * другая авторизация: у контура свой домен в РФ и разовый пропуск вместо
 * JWT приложения. Смешать их в одном клиенте значит однажды случайно
 * отправить ИНН на основной бэкенд — а туда его отправлять нельзя (152-ФЗ
 * ст. 18 ч. 5: первичная запись обязана произойти в РФ).</p>
 *
 * <p>Значения контур наружу не отдаёт: прочитать свой ИНН обратно нельзя ни
 * этим модулем, ни каким-либо другим — такого эндпоинта не существует.</p>
 */

/** Пропуск живёт минуты; держим его на время заполнения формы. */
let cached: { ticket: PayoutDetailsTicket; fetchedAt: number } | null = null;

/** Запас до истечения: лучше взять новый, чем упасть на отправке. */
const EXPIRY_MARGIN_MS = 60_000;

/**
 * CORE-1668 — сколько ждём ответа контура. Без своего предела `fetch` висит
 * до сетевого таймаута устройства: когда нода контура перестала отвечать
 * (11.10.2026), экран десятки секунд крутил индикатор, прежде чем сказать
 * «недоступно». Форма маленькая, запрос один — восьми секунд хватает с запасом.
 */
export const CONTOUR_TIMEOUT_MS = 8_000;

export class ContourError extends Error {
    constructor(public readonly code: ContourErrorCode, public readonly status: number) {
        super(code);
        this.name = 'ContourError';
    }
}

async function getTicket(force = false): Promise<PayoutDetailsTicket> {
    if (!force && cached) {
        const expiresAt = new Date(cached.ticket.expiresAtUtc).getTime();
        if (Number.isFinite(expiresAt) && expiresAt - Date.now() > EXPIRY_MARGIN_MS) {
            return cached.ticket;
        }
    }

    const ticket = await api.getPayoutDetailsTicket();
    cached = { ticket, fetchedAt: Date.now() };
    return ticket;
}

/** Сбросить пропуск — например, при выходе из экрана. */
export function forgetTicket(): void {
    cached = null;
}

async function call<T>(
    path: string,
    init: RequestInit,
    retryOnUnauthorized = true,
): Promise<T> {
    const ticket = await getTicket();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CONTOUR_TIMEOUT_MS);
    let response: Response;
    try {
        response = await fetch(`${ticket.contourUrl.replace(/\/$/, '')}${path}`, {
            ...init,
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/json',
                'X-Contour-Ticket': ticket.ticket,
                ...(init.headers as Record<string, string> | undefined ?? {}),
            },
        });
    } catch (e) {
        // Молчащий контур — та же ошибка для экрана, что и любая другая:
        // «не получилось, попробуйте ещё раз». Статус 0 отличает «не дождались»
        // от ответа с кодом.
        if (controller.signal.aborted) throw new ContourError('UNKNOWN', 0);
        throw e;
    } finally {
        clearTimeout(timer);
    }

    // Пропуск мог истечь, пока человек заполнял форму: берём новый и пробуем
    // ещё раз. Один раз — если и он не подошёл, дело не в сроке.
    if (response.status === 401 && retryOnUnauthorized) {
        await getTicket(true);
        return call<T>(path, init, false);
    }

    if (!response.ok) {
        let code: ContourErrorCode = 'UNKNOWN';
        try {
            const body = await response.json();
            if (typeof body?.code === 'string') code = body.code as ContourErrorCode;
        } catch { /* тело может быть пустым — код останется UNKNOWN */ }
        throw new ContourError(code, response.status);
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
}

/** Что уже заполнено. Значений в ответе нет по построению. */
export async function getPayoutDetailsStatus(): Promise<PayoutDetailsStatus> {
    return call<PayoutDetailsStatus>('/api/contour/payees/me/status', { method: 'GET' });
}

/** Отправить форму. Данные уходят в РФ, минуя основной бэкенд. */
export async function submitPayoutDetails(
    submission: PayoutDetailsSubmission,
): Promise<{ status: string }> {
    return call<{ status: string }>('/api/contour/payees/me', {
        method: 'PUT',
        body: JSON.stringify(submission),
    });
}

/** Отозвать согласие: выплаты закрываются, значения стираются сразу. */
export async function revokeConsent(): Promise<void> {
    await call<unknown>('/api/contour/payees/me/consent/revoke', { method: 'POST' });
}

/** Удалить данные по требованию субъекта. История начислений не трогается. */
export async function erasePayoutDetails(): Promise<void> {
    await call<unknown>('/api/contour/payees/me', { method: 'DELETE' });
}

/**
 * SHA-256 текста согласия в hex.
 *
 * <p>Контур хранит и версию, и хэш: версии мало, потому что текст правят, а
 * доказывать придётся тот экземпляр, под которым человек нажал кнопку.</p>
 */
export async function hashConsentText(text: string): Promise<string> {
    return Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, text);
}
